import { jsPDF } from "jspdf";
import { logoBase64 } from "@/utils/reusables/pdfLogo";
import reusableBase from "@/utils/composables/format";

export default function pdfGenerateHandler() {
  const { dateFormatMonthDateYear } = reusableBase();
  const exportPDF = (item) => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 20;

    // ================= WATERMARK =================
    const addWatermark = (text = "TEMPORARY") => {
      const fontSize = 70;
      const angle = 30;
      const rad = (angle * Math.PI) / 180;

      doc.saveGraphicsState();
      doc.setGState(new doc.GState({ opacity: 0.15 }));

      doc.setFont("Times", "bold");
      doc.setFontSize(fontSize);
      doc.setTextColor(150, 150, 150);

      const textWidth = doc.getTextWidth(text); // in mm
      const halfCapHeight = fontSize * 0.3528 * 0.33; // approx half of cap height in mm

      const cx = pageWidth / 2;
      const cy = pageHeight / 2;

      // Start point so the rotated text is centered on the page
      const startX =
        cx - (textWidth / 2) * Math.cos(rad) + halfCapHeight * Math.sin(rad);
      const startY =
        cy + (textWidth / 2) * Math.sin(rad) + halfCapHeight * Math.cos(rad);

      doc.text(text, startX, startY, { angle });

      doc.restoreGraphicsState();
      doc.setTextColor(0, 0, 0);
    };

    // ================= HEADER =================
    const addHeader = () => {
      const logoWidth = 120;
      const logoHeight = 30;
      const logoX = (pageWidth - logoWidth) / 2;

      doc.addImage(logoBase64, "JPEG", logoX, 10, logoWidth, logoHeight);

      let y = 38;

      doc.setFont("Times", "normal");
      doc.setFontSize(11);

      const headerLines = [
        "4th Floor PCCI Corporate Centre, 118 L.P. Leviste Street, Salcedo Village, 1209 Makati City.",
        "Telephone Nos.: (02) 8528-0480;",
        "Mobile Nos: 0922-822-1033 to 35; 0917-8518645; 0998-5869439",
        "Email Address: customerservices@iwc.com.ph",
      ];

      headerLines.forEach((line) => {
        doc.text(line, pageWidth / 2, y, { align: "center" });
        y += 6;
      });

      // Title
      doc.setFont("Times", "bold");
      doc.setFontSize(14);
      doc.text("TEMPORARY", pageWidth / 2, y + 12, {
        align: "center",
      });
      doc.text("LETTER OF AUTHORIZATION", pageWidth / 2, y + 20, {
        align: "center",
      });
    };

    // ================= DETAILS (Two Column) =================
    const addDetails = () => {
      let y = 100;
      const labelX = marginX;
      const valueX = marginX + 40;
      const maxWidth = pageWidth - valueX - marginX;

      doc.setFont("Times", "normal");
      doc.setFontSize(11);

      const rows = [
        ["Name of Provider:", item.provider_name],
        ["IWC Card Number:", item.iwccardno],
        ["Name of Cardholder:", item.membername],
        ["Approval Number:", item.approvalno],
        ["Date:", dateFormatMonthDateYear(item.admit_date)],
      ];

      rows.forEach(([label, value]) => {
        doc.text(label, labelX, y);

        doc.setFont("Times", "normal");

        const lines = doc.splitTextToSize(String(value ?? ""), maxWidth);
        doc.text(lines, valueX, y);

        doc.setFont("Times", "normal");

        y += lines.length * 6 + 2;
      });

      // Divider
      doc.setLineWidth(0.5);
      doc.line(marginX, y + 2, pageWidth - marginX, y + 2);

      return y + 12;
    };

    // ================= CONSULTATION =================
    const addConsultation = (startY) => {
      let y = startY;

      const pageWidth = doc.internal.pageSize.getWidth();
      const rightMargin = 20;

      doc.setFont("Times", "bold");
      doc.setFontSize(11);
      doc.text("CONSULTATION", marginX, y);

      y += 6;

      doc.setFont("Times", "normal");
      doc.text(String(item.provider_pf_category ?? ""), marginX + 5, y);

      const amountText = "";
      const textWidth = doc.getTextWidth(amountText);
      doc.text(amountText, pageWidth - rightMargin - textWidth, y);

      return y + 20;
    };

    // ================= VALIDITY NOTE =================
    const addValidityNote = (startY) => {
      // Extra space above the validity lines (pushes them lower)
      const spaceAboveValidity = 15;
      // Space between the validity lines and the PROVISIONAL note
      const spaceBelowValidity = 20;

      let y = startY + spaceAboveValidity;

      doc.setFont("Times", "normal");
      doc.setFontSize(10);
      doc.setTextColor(120, 120, 120);

      doc.text("This letter of authorization is ", marginX, y);

      const textWidth = doc.getTextWidth("This letter of authorization is ");
      doc.setTextColor(255, 0, 0);
      doc.text("valid for three (3) days", marginX + textWidth, y);

      doc.setTextColor(120, 120, 120);
      const afterWidth = doc.getTextWidth("valid for three (3) days");
      doc.text(
        " from the date of issuance.",
        marginX + textWidth + afterWidth,
        y
      );

      y += 6;

      doc.text(
        "If not availed within three (3) days, it shall be considered void.",
        marginX,
        y
      );

      y += spaceBelowValidity;

      // ---------- Mixed bold / normal note with word wrapping ----------
      const boldText = "THIS LOA IS PROVISIONAL.";
      const normalText =
        "Present it with your HMO card and valid ID to the hospital’s HMO Department for verification before availing of services.";

      const tokens = [
        ...boldText.split(" ").map((w) => ({ w, style: "bold" })),
        ...normalText.split(" ").map((w) => ({ w, style: "normal" })),
      ];

      const noteFontSize = 12;
      const lineHeight = 6.5; // mm between lines
      const maxX = pageWidth - marginX;
      let x = marginX;

      doc.setFontSize(noteFontSize);
      doc.setTextColor(0, 0, 0); // black

      tokens.forEach(({ w, style }) => {
        doc.setFont("Times", style);
        const wordWidth = doc.getTextWidth(w);

        doc.setFont("Times", "normal");
        const spaceWidth = doc.getTextWidth(" ");

        // Wrap to next line if the word doesn't fit
        if (x + wordWidth > maxX && x > marginX) {
          x = marginX;
          y += lineHeight;
        }

        doc.setFont("Times", style);
        doc.text(w, x, y);

        x += wordWidth + spaceWidth;
      });

      doc.setFont("Times", "normal");
      doc.setTextColor(0, 0, 0);
    };

    // ================= FOOTER =================
    const addFooter = () => {
      doc.setFont("Times", "normal");
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);

      doc.text(
        `Valid until: ${dateFormatMonthDateYear(item.date_validity)}`,
        marginX,
        pageHeight - 10
      );

      doc.text(`Page 1 of 1`, pageWidth - marginX, pageHeight - 10, {
        align: "right",
      });
    };

    // ================= GENERATE =================
    addWatermark("TEMPORARY"); // drawn first so it sits behind the content
    addHeader();
    const afterDetailsY = addDetails();
    const afterConsultY = addConsultation(afterDetailsY);
    addValidityNote(afterConsultY);
    addFooter();

    doc.save(`LOA_${item.approvalno}.pdf`);

    console.log("Download It");
  };

  return { exportPDF, dateFormatMonthDateYear };
}
